import { MakeLogicType, actions, kea, key, path, props, reducers, selectors } from 'kea'
import { router, urlToAction } from 'kea-router'

import { appMetricsLogic } from 'lib/components/AppMetrics/appMetricsLogic'
import { Scene } from 'scenes/sceneTypes'
import { urls } from 'scenes/urls'

import { SIDE_PANEL_CONTEXT_KEY, SidePanelSceneContext } from '~/layout/navigation-3000/sidepanel/types'
import { ActivityScope, Breadcrumb } from '~/types'

export const WorkflowTabs = ['workflow', 'invocations', 'metrics', 'assets', 'history'] as const
export type WorkflowTab = (typeof WorkflowTabs)[number]

export interface WorkflowSceneLogicProps {
    id?: string
    tab?: WorkflowTab
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowSceneLogicValues {
    breadcrumbs: Breadcrumb[]
    currentTab: WorkflowTab
    sidePanelContext: SidePanelSceneContext | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowSceneLogicActions {
    setCurrentTab: (tab: WorkflowTab) => {
        tab: 'assets' | 'history' | 'invocations' | 'metrics' | 'workflow'
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowSceneLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        breadcrumbs: (arg: WorkflowSceneLogicProps['id']) => Breadcrumb[]
        sidePanelContext: (arg: WorkflowSceneLogicProps['id']) => SidePanelSceneContext | null
    }
}

export type workflowSceneLogicType = MakeLogicType<
    workflowSceneLogicValues,
    workflowSceneLogicActions,
    WorkflowSceneLogicProps,
    workflowSceneLogicMeta
>

export const workflowSceneLogic = kea<workflowSceneLogicType>([
    path(['products', 'workflows', 'frontend', 'workflowSceneLogic']),
    props({ id: 'new' } as WorkflowSceneLogicProps),
    key((props) => `workflow-scene-${props.id || 'new'}`),
    actions({
        setCurrentTab: (tab: WorkflowTab) => ({ tab }),
    }),
    reducers({
        currentTab: [
            'workflow' as WorkflowTab,
            {
                setCurrentTab: (_, { tab }) => tab,
            },
        ],
    }),
    selectors({
        breadcrumbs: [
            () => [(_, props) => props.id as WorkflowSceneLogicProps['id']],
            (id: WorkflowSceneLogicProps['id']): Breadcrumb[] => {
                return [
                    {
                        key: [Scene.Workflows, 'workflows'],
                        name: 'Workflows',
                        path: urls.workflows('workflows'),
                        iconType: 'workflows',
                    },
                    {
                        key: Scene.Workflow,
                        name: id == 'new' ? 'New workflow' : 'Manage workflow',
                        iconType: 'workflows',
                    },
                ]
            },
        ],
        // Drives the side panel: surfaces the Access control (and Activity) tab for a saved workflow.
        // Must live on the scene logic — sidePanelContextLogic reads SIDE_PANEL_CONTEXT_KEY off the
        // scene's registered logic, not workflowLogic.
        [SIDE_PANEL_CONTEXT_KEY]: [
            () => [(_, props) => props.id as WorkflowSceneLogicProps['id']],
            (id: WorkflowSceneLogicProps['id']): SidePanelSceneContext | null =>
                id && id !== 'new'
                    ? {
                          activity_scope: ActivityScope.HOG_FLOW,
                          activity_item_id: id,
                          access_control_resource: 'hog_flow',
                          access_control_resource_id: id,
                      }
                    : null,
        ],
    }),
    urlToAction(({ actions, values }) => ({
        '/workflows/:id/:tab': ({ id, tab }, searchParams) => {
            // The invocations view used to live on the 'logs' tab. Redirect old /logs deep links
            // (and the batch-jobs URL trigger) to /invocations so they keep resolving, preserving
            // any query params (e.g. the metrics-page drill-down's search/level filters).
            if (tab === 'logs') {
                router.actions.replace(urls.workflow(id ?? 'new', 'invocations'), searchParams)
                return
            }
            if (tab !== values.currentTab) {
                actions.setCurrentTab(tab as WorkflowTab)
            }
            // Keep the run-metrics step drilldown in sync with ?action= so refresh, shared links, and
            // browser back/forward restore the selected step (its date range/interval stay untouched).
            // Batch metrics render several per-job tables, so they keep their own local selection.
            if (tab === 'metrics' && id && id !== 'new') {
                appMetricsLogic
                    .findMounted({ logicKey: `hog-flow-metrics-${id}` })
                    ?.actions.setParams({ instanceId: (searchParams.action as string) || undefined })
            }
        },
    })),
])
